import type { FC } from "react";
import { Link } from "react-router";
import { Container } from "../components/Container";
import { Notice } from "../components/Notice";
import { PageHeading } from "../components/PageHeading";
import { SectionHeading } from "../components/SectionHeading";

/**
 * Amazon・楽天市場・Yahoo! ショッピングでご購入のお客様向けのお問い合わせ導線。
 *
 * これらモール経由のご注文は Shopify と紐づかないため、本マイページからは
 * 確認 / キャンセル / お届け先変更 / 返品申請いずれも操作できない。
 * 各モールの店舗ページからお問い合わせいただくよう誘導する。
 *
 * URL 編集ポリシー: 各モールの問い合わせフォーム URL は店舗側で変更される可能性があるため
 * 本ファイル先頭の `EXTERNAL_MALLS` 定数で一元管理する (= レビュー時に URL の正しさを
 * 1 箇所で確認できるように)。
 */
type Mall = {
  id: "amazon" | "rakuten" | "yahoo";
  name: string;
  url: string;
};

const EXTERNAL_MALLS: readonly Mall[] = [
  {
    id: "amazon",
    name: "Amazon",
    url: "https://amazon.co.jp/gp/help/contact-seller/contact-seller.html?sellerID=A26I3NBC42T8VK&marketplaceID=A1VC38T7YXB528",
  },
  {
    id: "rakuten",
    name: "楽天市場",
    url: "https://inquiry.my.rakuten.co.jp/shop/425156",
  },
  {
    id: "yahoo",
    name: "Yahoo! ショッピング",
    url: "https://talk.shopping.yahoo.co.jp/contact/survaqstore",
  },
];

export const ExternalMallContactPage: FC = () => (
  <Container>
    <PageHeading
      title="Amazon・楽天市場・Yahoo! ショッピングでご購入の方"
      description="各モールでご購入されたご注文について、お問い合わせ先をご案内いたします。"
    />

    <Notice variant="info" className="mb-8">
      Amazon・楽天市場・Yahoo! ショッピングでのご注文は、本マイページの対象外となっております。
      お手数ですが、ご購入されたモール内のお問い合わせ窓口よりご連絡ください。
    </Notice>

    <section className="mb-10">
      <SectionHeading title="ご購入されたモールをお選びください" />
      <ul className="space-y-4 py-4">
        {EXTERNAL_MALLS.map((mall) => (
          <li key={mall.id}>
            <MallRow mall={mall} />
          </li>
        ))}
      </ul>
    </section>

    <footer className="mt-12 border-neutral-200 border-t pt-6 text-center text-neutral-500 text-xs">
      <p className="mb-2">SurvaQストア / CALQS公式サイトでのご注文に関するお問い合わせは</p>
      <Link to="/contact" className="underline underline-offset-4 hover:text-neutral-900">
        こちらのお問い合わせ先
      </Link>
      <p className="mt-4">
        <Link to="/" className="underline underline-offset-4 hover:text-neutral-900">
          マイページに戻る
        </Link>
      </p>
    </footer>
  </Container>
);

const MallRow: FC<{ mall: Mall }> = ({ mall }) => (
  <div className="rounded border border-neutral-200 bg-white p-4">
    <p className="mb-2 font-medium text-neutral-900 text-sm">{mall.name}</p>
    <a
      href={mall.url}
      target="_blank"
      rel="noreferrer"
      className="inline-block text-neutral-900 text-sm underline underline-offset-4 hover:text-neutral-700"
    >
      {mall.name} のお問い合わせフォームを開く
    </a>
  </div>
);
